import React, { useState, useEffect, useMemo } from "react";
import { 
    Card, Table, Row, Col, Form, InputGroup, Button, Pagination, 
    Dropdown, Modal, Alert, Spinner, Badge 
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatNumber, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import './Clients.css';

export default function Clients() {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [sortField, setSortField] = useState('raison_sociale');
    const [sortDirection, setSortDirection] = useState('asc');
    const [selectedClients, setSelectedClients] = useState([]);
    const [exportLoading, setExportLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [stats, setStats] = useState(null);

    useEffect(() => {
        fetchClients();
        fetchStats();
    }, []);

    const fetchClients = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/clients.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem("token");
                    localStorage.removeItem("user");
                    navigate("/pages/signin2");
                    throw new Error("Session expirée");
                }
                throw new Error(`Erreur HTTP: ${response.status}`);
            }
            
            const result = await response.json();
            setData(result.data || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/clients.php?action=statistiques`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const result = await response.json();
                setStats(result.data);
            }
        } catch (err) {
            console.error("Erreur chargement stats", err);
        }
    };

    const fetchClientDetails = async (id) => {
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/clients.php?id=${id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const result = await response.json();
                setSelectedClient(result.data);
                setShowModal(true);
            }
        } catch (err) {
            alert("Erreur lors du chargement des détails");
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce client ?")) return;
        
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(`${API_BASE_URL}/clients.php?id=${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            const result = await response.json();
            
            if (result.success) {
                fetchClients();
                fetchStats();
                alert(result.message);
            } else {
                alert(result.message);
            }
        } catch (err) {
            alert("Erreur lors de la suppression");
        }
    };

    const getStatusBadge = (client) => {
        if (client.encours > 0) {
            return { variant: 'warning', text: 'En cours' };
        }
        if (client.total_achats > 0) {
            return { variant: 'success', text: 'Actif' };
        }
        return { variant: 'secondary', text: 'Inactif' };
    };

    const getSoldeClass = (solde) => {
        if (solde > 0) return 'text-danger';
        if (solde < 0) return 'text-success';
        return 'text-secondary';
    };

    const filteredAndSortedItems = useMemo(() => {
        let filtered = data.filter(item =>
            item.raison_sociale?.toLowerCase().includes(filterText.toLowerCase()) ||
            item.code_client?.toLowerCase().includes(filterText.toLowerCase()) ||
            item.email?.toLowerCase().includes(filterText.toLowerCase()) ||
            item.telephone?.includes(filterText)
        );

        filtered.sort((a, b) => {
            let aValue = a[sortField];
            let bValue = b[sortField];
            
            if (sortDirection === 'asc') {
                return aValue > bValue ? 1 : -1;
            } else {
                return aValue < bValue ? 1 : -1;
            }
        });

        return filtered;
    }, [filterText, sortField, sortDirection, data]);

    const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            setSelectedClients(currentItems.map(c => c.id_client));
        } else {
            setSelectedClients([]);
        }
    };

    const handleSelectClient = (clientId) => {
        setSelectedClients(prev => 
            prev.includes(clientId) 
                ? prev.filter(id => id !== clientId)
                : [...prev, clientId]
        );
    };

    const isAllSelected = currentItems.length > 0 && selectedClients.length === currentItems.length;
    const isSomeSelected = selectedClients.length > 0 && selectedClients.length < currentItems.length;

    const handleExport = (format) => {
        setExportLoading(true);
        const dataToExport = selectedClients.length > 0
            ? data.filter(c => selectedClients.includes(c.id_client))
            : filteredAndSortedItems;

        if (!dataToExport.length) { 
            alert("Aucune donnée à exporter"); 
            setExportLoading(false); 
            return; 
        }

        if (format === "csv") {
            const headers = ["Code","Raison sociale","Téléphone","Email","Total achats","Encours","Plafond","Statut"];
            const csvContent = [
                "\uFEFF" + headers.join(";"),
                ...dataToExport.map(c => [
                    c.code_client,
                    c.raison_sociale,
                    c.telephone || '',
                    c.email || '',
                    c.total_achats || 0,
                    c.encours || 0,
                    c.plafond_credit || 0,
                    getStatusBadge(c).text
                ].map(v => `"${v}"`).join(";"))
            ].join("\n");
            downloadFile(csvContent, "text/csv;charset=utf-8;", `export-clients-${Date.now()}.csv`);

        } else if (format === "excel") {
            const ws = XLSX.utils.json_to_sheet(dataToExport.map(c => ({
                Code: c.code_client,
                "Raison sociale": c.raison_sociale,
                Téléphone: c.telephone,
                Email: c.email,
                "Total achats": c.total_achats,
                Encours: c.encours,
                Plafond: c.plafond_credit,
                Statut: getStatusBadge(c).text
            })));
            
            const columnWidths = [
                { wch: 12 }, { wch: 30 }, { wch: 15 }, { wch: 25 }, 
                { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 12 }
            ];
            ws['!cols'] = columnWidths;
            
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Clients");
            XLSX.writeFile(wb, `export-clients-${Date.now()}.xlsx`);

        } else if (format === "pdf") {
            generatePDF(dataToExport);
        }

        setExportLoading(false);
    };

    const generatePDF = (data) => {
        const doc = new jsPDF();
        
        const primaryColor = [41, 128, 185];
        const headerColor = [240, 240, 240];
        const alternateColor = [248, 249, 250];
        
        doc.setFillColor(...primaryColor);
        doc.rect(0, 0, 210, 25, 'F');
        
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(16);
        doc.setFont("helvetica", "bold");
        doc.text("LISTE DES CLIENTS", 105, 15, { align: 'center' });
        
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.text(`Export généré le: ${new Date().toLocaleDateString('fr-FR')}`, 15, 22);
        doc.text(`Total: ${data.length} client(s)`, 180, 22, { align: 'right' });
        
        const pageWidth = 210;
        const margin = 15;
        let yPosition = 35;
        
        const colWidths = [20, 45, 25, 25, 25, 20, 20];
        const headers = ["CODE", "RAISON SOCIALE", "TÉLÉPHONE", "EMAIL", "TOTAL ACHATS", "ENCOURS", "STATUT"];
        
        doc.setFillColor(...headerColor);
        doc.rect(margin, yPosition - 4, pageWidth - 2 * margin, 8, 'F');
        
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(8);
        doc.setFont("helvetica", "bold");
        
        let xPosition = margin;
        headers.forEach((header, index) => {
            doc.text(header, xPosition + 2, yPosition);
            xPosition += colWidths[index];
        });
        
        yPosition += 6;
        
        doc.setFontSize(7);
        doc.setFont("helvetica", "normal");
        
        data.forEach((item, index) => {
            if (yPosition > 270) {
                doc.addPage();
                yPosition = 20;
                
                doc.setFillColor(...headerColor);
                doc.rect(margin, yPosition - 4, pageWidth - 2 * margin, 8, 'F');
                
                doc.setTextColor(0, 0, 0);
                doc.setFontSize(8);
                doc.setFont("helvetica", "bold");
                
                let newX = margin;
                headers.forEach((header, idx) => {
                    doc.text(header, newX + 2, yPosition);
                    newX += colWidths[idx];
                });
                
                yPosition += 6;
                doc.setFontSize(7);
                doc.setFont("helvetica", "normal");
            }
            
            if (index % 2 === 0) {
                doc.setFillColor(...alternateColor);
                doc.rect(margin, yPosition - 4, pageWidth - 2 * margin, 6, 'F');
            }
            
            doc.setTextColor(0, 0, 0);
            
            const rowData = [
                item.code_client,
                item.raison_sociale.length > 20 ? item.raison_sociale.substring(0, 20) + '...' : item.raison_sociale,
                item.telephone || '-',
                item.email ? (item.email.length > 15 ? item.email.substring(0, 15) + '...' : item.email) : '-',
                formatCurrency(item.total_achats || 0),
                formatCurrency(item.encours || 0),
                getStatusBadge(item).text
            ];
            
            let currentX = margin;
            rowData.forEach((cell, cellIndex) => {
                if (cellIndex === 5) {
                    const encours = item.encours || 0;
                    if (encours > 0) doc.setTextColor(220, 53, 69);
                    else doc.setTextColor(40, 167, 69);
                } else {
                    doc.setTextColor(0, 0, 0);
                }
                
                doc.text(cell, currentX + 1, yPosition);
                currentX += colWidths[cellIndex];
            });
            
            yPosition += 6;
        });
        
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setDrawColor(200, 200, 200);
            doc.line(margin, 275, pageWidth - margin, 275);
            doc.setTextColor(100, 100, 100);
            doc.setFontSize(8);
            doc.setFont("helvetica", "normal");
            doc.text(`Page ${i} sur ${pageCount}`, 105, 282, { align: 'center' });
            doc.text("Système de Gestion", margin, 282);
            doc.text(new Date().getFullYear().toString(), pageWidth - margin, 282, { align: 'right' });
        }
        
        doc.save(`clients-${Date.now()}.pdf`);
    };

    const downloadFile = (content, type, filename) => {
        const blob = new Blob([content], { type });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        setTimeout(() => URL.revokeObjectURL(link.href), 100);
    };

    const SortableHeader = ({ field, children }) => (
        <th 
            style={{ cursor: 'pointer', userSelect: 'none' }}
            onClick={() => handleSort(field)}
        >
            {children}
            {sortField === field ? (
                <i className={`ri-caret-${sortDirection === 'asc' ? 'up' : 'down'}-fill ms-2`} style={{ fontSize: '12px' }}></i>
            ) : (
                <i className="ri-arrow-down-up ms-2" style={{ fontSize: '11px', opacity: 0.5 }}></i>
            )}
        </th>
    );

    if (loading && !data.length) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="loading-animation mb-4">
                                <div className="loading-ring"><div className="loading-ring-inner"></div></div>
                                <i className="ri-group-line loading-icon"></i>
                            </div>
                            <h5 className="fw-bold text-primary mb-2">Chargement des clients</h5>
                            <p className="text-muted small mb-0">Récupération des données en cours...</p>
                            <div className="mx-auto mt-4" style={{ width: '200px' }}>
                                <div className="progress" style={{ height: '3px', background: '#e9ecef' }}>
                                    <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: '100%', background: 'linear-gradient(90deg, #4361ee, #10b981, #f59e0b)' }} />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
                <style>{`
                    .loading-animation { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                    .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; background: linear-gradient(135deg, #4361ee, #10b981); animation: pulse-ring 1.5s ease-in-out infinite; }
                    .loading-ring-inner { position: absolute; top: 5px; left: 5px; right: 5px; bottom: 5px; border-radius: 50%; background: white; }
                    .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: #4361ee; z-index: 2; animation: pulse-icon 1.5s ease-in-out infinite; }
                    @keyframes pulse-ring { 0% { transform: scale(0.9); opacity: 0.7; } 50% { transform: scale(1.1); opacity: 0.3; } 100% { transform: scale(0.9); opacity: 0.7; } }
                    @keyframes pulse-icon { 0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; } 50% { transform: translate(-50%, -50%) scale(1.1); opacity: 0.8; } }
                `}</style>
            </>
        );
    }

    if (error && !data.length) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                                <p className="mb-0 small">{error}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={fetchClients}>
                                    <i className="ri-refresh-line me-1"></i>Réessayer
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                
                {/* En-tête moderne */}
                <div className="d-md-flex align-items-center justify-content-between mb-4">
                    <div>
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <div className="p-2 bg-primary bg-opacity-10 rounded-3">
                                <i className="ri-group-line fs-20 text-primary"></i>
                            </div>
                            <div>
                                <h4 className="main-title mb-0 fw-bold">Gestion des clients</h4>
                                <p className="text-muted small mb-0">Gérez votre portefeuille clients</p>
                            </div>
                        </div>
                    </div>
                    <div className="d-flex gap-2 mt-3 mt-md-0">
                        <Button 
                            variant="outline-primary" 
                            size="sm"
                            onClick={fetchClients}
                            disabled={loading}
                            className="rounded-pill px-3 d-flex align-items-center gap-1"
                        >
                            <i className={`ri-refresh-line ${loading ? 'fa-spin' : ''}`}></i>
                            Actualiser
                        </Button>
                        
                        <Dropdown align="end">
                            <Dropdown.Toggle variant="outline-secondary" size="sm" className="rounded-pill px-3 d-flex align-items-center gap-1" disabled={exportLoading}>
                                <i className="ri-download-line"></i>
                                Exporter
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item onClick={() => handleExport('csv')}>
                                    <i className="ri-file-csv-line text-info me-2"></i> Export CSV
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => handleExport('excel')}>
                                    <i className="ri-file-excel-line text-success me-2"></i> Export Excel
                                </Dropdown.Item>
                                <Dropdown.Item onClick={() => handleExport('pdf')}>
                                    <i className="ri-file-pdf-line text-danger me-2"></i> Export PDF
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                        
                        <Button 
                         style={{background:'#21ACCD',border:'0',color:'white'}}
                            as={Link} 
                            to="/ventes/clients/ajouter" 
                            variant="#21ACCD"
                            size="sm"
                            className="rounded-pill px-3 d-flex align-items-center gap-1"
                        >
                            <i className="ri-add-line"></i>
                            Nouveau client
                        </Button>
                    </div>
                </div>

                {/* KPI Cards */}
                {stats && (
                    <Row className="g-3 mb-4">
                        <Col sm="6" xl="3">
                            <div className="kpi-card kpi-primary">
                                <div className="kpi-icon"><i className="ri-group-line fs-24"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Total clients</span>
                                    <h3 className="kpi-value">{formatNumber(stats.total_clients || 0)}</h3>
                                    <span className="kpi-unit">{stats.nouveaux_mois || 0} nouveaux ce mois</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-card kpi-danger">
                                <div className="kpi-icon"><i className="ri-alert-line fs-24"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Clients débiteurs</span>
                                    <h3 className="kpi-value">{formatNumber(stats.clients_debiteurs || 0)}</h3>
                                    <span className="kpi-unit">Solde moyen: {formatCurrency(stats.solde_moyen || 0)}</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-card kpi-success">
                                <div className="kpi-icon"><i className="ri-bank-card-line fs-24"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Avec crédit</span>
                                    <h3 className="kpi-value">{formatNumber(stats.clients_avec_credit || 0)}</h3>
                                    <span className="kpi-unit">Plafond disponible</span>
                                </div>
                            </div>
                        </Col>
                        <Col sm="6" xl="3">
                            <div className="kpi-card kpi-info">
                                <div className="kpi-icon"><i className="ri-calendar-line fs-24"></i></div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Nouveaux</span>
                                    <h3 className="kpi-value">{formatNumber(stats.nouveaux_jour || 0)}</h3>
                                    <span className="kpi-unit">Aujourd'hui</span>
                                </div>
                            </div>
                        </Col>
                    </Row>
                )}

                {/* Filtres */}
                <Card className="border-0 shadow-sm rounded-3 mb-4">
                    <Card.Body className="py-3 px-4">
                        <Row className="align-items-center g-3">
                            <Col md="6">
                                <div className="position-relative">
                                    <i className="ri-search-line position-absolute top-50 start-0 translate-middle-y ms-3 text-muted fs-14"></i>
                                    <Form.Control
                                        type="text"
                                        placeholder="Rechercher par nom, code, email, téléphone..."
                                        value={filterText}
                                        onChange={e => setFilterText(e.target.value)}
                                        className="ps-5 rounded-pill"
                                    />
                                </div>
                            </Col>
                            <Col md="4">
                                <div className="d-flex gap-2">
                                    <Form.Select 
                                        value={itemsPerPage} 
                                        onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                        className="rounded-pill px-3"
                                        style={{ width: '120px' }}
                                    >
                                        <option value={5}>5 lignes</option>
                                        <option value={10}>10 lignes</option>
                                        <option value={25}>25 lignes</option>
                                        <option value={50}>50 lignes</option>
                                    </Form.Select>
                                    {filterText && (
                                        <Button 
                                            variant="outline-secondary" 
                                            size="sm"
                                            onClick={() => setFilterText('')}
                                            className="rounded-pill px-3"
                                        >
                                            <i className="ri-close-line"></i> Effacer
                                        </Button>
                                    )}
                                </div>
                            </Col>
                            <Col md="2">
                                <div className="text-end">
                                    <span className="badge bg-light text-dark rounded-pill px-3 py-2">
                                        <i className="ri-stack-line me-1"></i>
                                        {filteredAndSortedItems.length} client(s)
                                    </span>
                                </div>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Sélection en masse */}
                {selectedClients.length > 0 && (
                    <div className="d-flex justify-content-between align-items-center mb-3 p-2 bg-light rounded-3">
                        <span className="small text-primary">
                            <i className="ri-checkbox-circle-line me-1"></i>
                            {selectedClients.length} client(s) sélectionné(s)
                        </span>
                        <Button variant="link" size="sm" className="text-danger p-0" onClick={() => setSelectedClients([])}>
                            <i className="ri-close-line"></i> Effacer
                        </Button>
                    </div>
                )}

                {/* Tableau des clients */}
                <Card className="border-0 shadow-sm rounded-0 overflow-hidden">
                    <Card.Header className="border-0 py-3" style={{background:'#21ACCD'}}>
                        <div className="d-flex justify-content-between align-items-center">
                            <div className="d-flex align-items-center">
                                <i className="ri-building-line text-white me-2 fs-5"></i>
                                <span className="fw-semibold text-white">Liste des Clients</span>
                                <Badge bg="light" text="dark" className="ms-2 rounded-pill">
                                    {filteredAndSortedItems.length} au total
                                </Badge>
                            </div>
                            {selectedClients.length > 0 && (
                                <Badge bg="primary" className="rounded-pill">
                                    {selectedClients.length} sélectionné(s)
                                </Badge>
                            )}
                        </div>
                    </Card.Header>
                    <Card.Body className="p-0">
                        <div className="table-responsive">
                            <Table hover className="align-middle mb-0">
                                <thead >
                                    <tr >
                                        <th style={{ width: '45px' }} className="ps-4">
                                            <input 
                                                type="checkbox" 
                                                className="form-check-input rounded-checkbox" 
                                                checked={isAllSelected && filteredAndSortedItems.length > 0} 
                                                ref={input => { if (input) input.indeterminate = isSomeSelected; }} 
                                                onChange={handleSelectAll} 
                                            />
                                        </th>
                                        <SortableHeader field="code_client">Code</SortableHeader>
                                        <SortableHeader field="raison_sociale">Client</SortableHeader>
                                        <th>Contact</th>
                                        <SortableHeader field="encours" className="text-end">Encours</SortableHeader>
                                        <th className="text-center">Statut</th>
                                        <th className="text-center pe-4">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentItems.length > 0 ? currentItems.map((client) => {
                                        const status = getStatusBadge(client);
                                        return (
                                            <tr key={client.id_client}>
                                                <td className="ps-4">
                                                    <input 
                                                        type="checkbox" 
                                                        className="form-check-input rounded-checkbox" 
                                                        checked={selectedClients.includes(client.id_client)} 
                                                        onChange={() => handleSelectClient(client.id_client)} 
                                                    />
                                                </td>
                                                <td>
                                                    <span className="reference-badge">{client.code_client}</span>
                                                </td>
                                                <td>
                                                    <div className="fw-semibold">{client.raison_sociale}</div>
                                                    {client.numero_compte && <small className="text-muted">{client.numero_compte}</small>}
                                                </td>
                                                <td>
                                                    {client.telephone && <div className="d-flex align-items-center"><i className="ri-phone-line text-muted me-1 fs-10"></i>{client.telephone}</div>}
                                                    {client.email && <div className="d-flex align-items-center"><i className="ri-mail-line text-muted me-1 fs-10"></i>{client.email}</div>}
                                                    {!client.telephone && !client.email && <span className="text-muted">-</span>}
                                                </td>
                                                <td className="text-end">
                                                    <span className={`fw-bold ${getSoldeClass(client.encours)}`}>
                                                        {formatCurrency(client.encours || 0)}
                                                    </span>
                                                    {client.encours > 0 && <small className="d-block text-muted">{client.nb_factures || 0} facture(s)</small>}
                                                </td>
                                                <td className="text-center">
                                                    <span className="status-badge" style={{
                                                        background: status.variant === 'success' ? '#10b981' : status.variant === 'warning' ? '#f59e0b' : '#64748b',
                                                        color: status.variant === 'success' ? '#fff' : status.variant === 'warning' ? '#fff' : '#fff'
                                                    }}>
                                                        {status.text}
                                                    </span>
                                                </td>
                                                <td className="text-center pe-4">
                                                    <div className="d-flex gap-2 justify-content-center">
                                                        <Link 
    to={`/ventes/clients/${client.id_client}`}
    className="action-icon"
    title="Voir détails"
>
    <i className="ri-eye-line"></i>
</Link>
                                                        <button className="action-icon action-icon-primary" onClick={() => navigate(`/ventes/clients/modifier/${client.id_client}`)} title="Modifier">
                                                            <i className="ri-pencil-line"></i>
                                                        </button>
                                                        <button className="action-icon action-icon-danger" onClick={() => handleDelete(client.id_client)} title="Supprimer">
                                                            <i className="ri-delete-bin-line"></i>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    }) : (
                                        <tr>
                                            <td colSpan="7" className="text-center py-5">
                                                <i className="ri-inbox-line fs-48 text-muted mb-3 d-block"></i>
                                                <h6 className="mb-1">Aucun client trouvé</h6>
                                                <p className="text-muted small mb-0">Essayez de modifier vos critères de recherche</p>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>
                    </Card.Body>
                </Card>

                {/* Pagination */}
                {totalPages > 1 && !loading && (
                    <div className="d-flex justify-content-between align-items-center mt-4 px-3">
                        <div className="text-muted small">
                            Affichage de {startIndex + 1} à {Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)} sur {filteredAndSortedItems.length} clients
                            {selectedClients.length > 0 && (
                                <span className="ms-2 text-primary">• {selectedClients.length} sélectionné(s)</span>
                            )}
                        </div>
                        <Pagination className="mb-0">
                            <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p-1))} disabled={currentPage === 1} />
                            {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                let pageNum;
                                if (totalPages <= 5) pageNum = i + 1;
                                else if (currentPage <= 3) pageNum = i + 1;
                                else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                else pageNum = currentPage - 2 + i;
                                return (
                                    <Pagination.Item key={i} active={pageNum === currentPage} onClick={() => setCurrentPage(pageNum)}>
                                        {pageNum}
                                    </Pagination.Item>
                                );
                            })}
                            <Pagination.Next onClick={() => setCurrentPage(p => Math.min(totalPages, p+1))} disabled={currentPage === totalPages} />
                        </Pagination>
                    </div>
                )}

                <Footer />
            </div>

            {/* Modal Détails Client */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered className="detail-modal">
                <Modal.Header closeButton className="border-0 pt-4 px-4">
                    <div className="d-flex align-items-center gap-2">
                        <div className="p-2 rounded-3 bg-primary bg-opacity-10"><i className="ri-eye-line fs-20 text-primary"></i></div>
                        <Modal.Title className="fs-5 fw-bold">Détails du client</Modal.Title>
                    </div>
                </Modal.Header>
                <Modal.Body className="px-4 pb-4">
                    {selectedClient && (
                        <>
                            <div className="summary-card p-3 mb-4 rounded-3">
                                <div className="d-flex justify-content-between align-items-start">
                                    <div>
                                        <h5 className="fw-bold mb-1">{selectedClient.raison_sociale}</h5>
                                        <p className="text-muted small mb-0">
                                            Code: {selectedClient.code_client} • Inscrit le {formatDate(selectedClient.date_creation)}
                                        </p>
                                    </div>
                                    <div className="text-end">
                                        <span className={`status-badge ${selectedClient.encours > 0 ? 'status-warning' : selectedClient.total_achats > 0 ? 'status-success' : 'status-secondary'}`}>
                                            {selectedClient.encours > 0 ? 'En cours' : selectedClient.total_achats > 0 ? 'Actif' : 'Inactif'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <Row className="g-3 mb-4">
                                <Col md={6}>
                                    <div className="info-card p-3 rounded-3">
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <i className="ri-phone-line text-primary"></i>
                                            <span className="fw-semibold small">Téléphone</span>
                                        </div>
                                        <p className="mb-0">{selectedClient.telephone || 'Non renseigné'}</p>
                                    </div>
                                </Col>
                                <Col md={6}>
                                    <div className="info-card p-3 rounded-3">
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <i className="ri-mail-line text-primary"></i>
                                            <span className="fw-semibold small">Email</span>
                                        </div>
                                        <p className="mb-0">{selectedClient.email || 'Non renseigné'}</p>
                                    </div>
                                </Col>
                                <Col md={12}>
                                    <div className="info-card p-3 rounded-3">
                                        <div className="d-flex align-items-center gap-2 mb-2">
                                            <i className="ri-map-pin-line text-primary"></i>
                                            <span className="fw-semibold small">Adresse</span>
                                        </div>
                                        <p className="mb-0">{selectedClient.adresse || 'Non renseignée'}</p>
                                    </div>
                                </Col>
                            </Row>

                            <div className="section-title mb-3">Identifiants juridiques</div>
                            <Row className="g-3 mb-4">
                                {[
                                    { icon: 'ri-bank-card-line', label: 'Numéro compte', value: selectedClient.numero_compte },
                                    { icon: 'ri-building-line', label: 'ICE', value: selectedClient.ice },
                                    { icon: 'ri-file-text-line', label: 'Patente', value: selectedClient.patente },
                                    { icon: 'ri-file-copy-line', label: 'RC', value: selectedClient.rc },
                                    { icon: 'ri-calculator-line', label: 'IF', value: selectedClient.identifiant_fiscal },
                                    { icon: 'ri-shield-line', label: 'CNSS', value: selectedClient.cnss }
                                ].map((item, index) => (
                                    <Col md={6} key={index}>
                                        <div className="info-item d-flex justify-content-between p-2 rounded-2 border">
                                            <span className="text-muted small">{item.label}</span>
                                            <span className="fw-semibold">{item.value || '-'}</span>
                                        </div>
                                    </Col>
                                ))}
                            </Row>

                            <div className="section-title mb-3">Informations financières</div>
                            <Row className="g-3 mb-4">
                                <Col md={4}>
                                    <div className="financial-card p-3 rounded-3 text-center">
                                        <div className="text-muted small mb-1">Total achats</div>
                                        <h5 className="mb-0 text-primary fw-bold">{formatCurrency(selectedClient.total_achats || 0)}</h5>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="financial-card p-3 rounded-3 text-center">
                                        <div className="text-muted small mb-1">Encours</div>
                                        <h5 className={`mb-0 fw-bold ${getSoldeClass(selectedClient.encours)}`}>{formatCurrency(selectedClient.encours || 0)}</h5>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="financial-card p-3 rounded-3 text-center">
                                        <div className="text-muted small mb-1">Plafond crédit</div>
                                        <h5 className="mb-0 text-info fw-bold">{formatCurrency(selectedClient.plafond_credit || 0)}</h5>
                                    </div>
                                </Col>
                            </Row>

                            {selectedClient.factures?.length > 0 && (
                                <>
                                    <div className="section-title mb-3">Dernières factures</div>
                                    <div className="table-responsive">
                                        <Table className="align-middle mb-0">
                                            <thead className="bg-primary">
                                                <tr>
                                                    <th>Facture</th>
                                                    <th>Date</th>
                                                    <th className="text-end">Montant</th>
                                                    <th className="text-end">Reste</th>
                                                    <th>Statut</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {selectedClient.factures.map(facture => (
                                                    <tr key={facture.id_facture}>
                                                        <td className="fw-semibold">{facture.numero_facture}</td>
                                                        <td>{formatDate(facture.date_facture)}</td>
                                                        <td className="text-end">{formatCurrency(facture.montant)}</td>
                                                        <td className="text-end text-danger fw-bold">{formatCurrency(facture.reste)}</td>
                                                        <td><Badge bg={facture.statut === 'payee' ? 'success' : facture.statut === 'payee_partielle' ? 'warning' : 'danger'} className="rounded-pill">{facture.statut}</Badge></td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                </>
                            )}

                            <div className="metadata-section mt-4 pt-3 border-top">
                                <div className="text-muted small">
                                    ID: #{selectedClient.id_client} • Créé le {formatDate(selectedClient.date_creation)}
                                    {selectedClient.date_modification && ` • Modifié le ${formatDate(selectedClient.date_modification)}`}
                                </div>
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="border-0 pb-4 px-4">
                    <Button variant="light" onClick={() => setShowModal(false)} className="rounded-pill px-4">Fermer</Button>
                    <Button variant="primary" className="rounded-pill px-4" as={Link} to={`/ventes/clients/modifier/${selectedClient?.id_client}`}>
                        <i className="ri-pencil-line me-2"></i>Modifier
                    </Button>
                </Modal.Footer>
            </Modal>

            <style>{`
                .kpi-card { background: white; border-radius: 0px; padding: 1rem; display: flex; align-items: center; gap: 1rem; box-shadow: 0 1px 3px rgba(0,0,0,0.05); transition: all 0.2s ease; height: 100%; border-left: 2px solid; }
                .kpi-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(0,0,0,0.08); }
                .kpi-primary { border-left-color: #4361ee; } .kpi-danger { border-left-color: #ef4444; } .kpi-success { border-left-color: #10b981; } .kpi-info { border-left-color: #0dcaf0; }
                .kpi-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; }
                .kpi-primary .kpi-icon { background: #e8f0fe; color: #4361ee; } .kpi-danger .kpi-icon { background: #fef2f2; color: #ef4444; }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; } .kpi-info .kpi-icon { background: #e2f3f5; color: #0dcaf0; }
                .kpi-content { flex: 1; } .kpi-label { font-size: 0.7rem; text-transform: uppercase; color: #6c757d; font-weight: 600; letter-spacing: 0.5px; }
                .kpi-value { font-size: 1.3rem; font-weight: 700; margin: 0.25rem 0; color: #1a3c5e; } .kpi-unit { font-size: 0.7rem; color: #6c757d; }
                .status-badge { display: inline-flex; align-items: center; padding: 0.25rem 0.75rem; border-radius: 20px; font-size: 0.75rem; font-weight: 500; }
                .status-success { background: #ecfdf5; color: #10b981; } .status-warning { background: #fffbeb; color: #f59e0b; } .status-secondary { background: #f1f5f9; color: #64748b; }
                .action-icon { width: 32px; height: 32px; border-radius: 8px; display: inline-flex; align-items: center; justify-content: center; background: #f8f9fa; border: 1px solid #e9ecef; color: #6c757d; transition: all 0.2s; cursor: pointer; }
                .action-icon:hover { background: #4361ee; border-color: #4361ee; color: white; }
                .action-icon-primary:hover { background: #4361ee; border-color: #4361ee; color: white; }
                .action-icon-danger:hover { background: #dc3545; border-color: #dc3545; color: white; }
                .reference-badge { background: #f8f9fa; padding: 0.2rem 0.6rem; border-radius: 6px; font-size: 0.7rem; font-family: monospace; }
                .info-card { background: #f8f9fa; border: 1px solid #e9ecef; transition: all 0.2s; }
                .info-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.05); }
                .financial-card { background: #f8f9fa; border: 1px solid #e9ecef; transition: all 0.2s; }
                .financial-card:hover { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.05); }
                .section-title { font-size: 0.85rem; font-weight: 600; color: #1a3c5e; border-left: 3px solid #4361ee; padding-left: 0.75rem; }
                .summary-card { background: #f8f9fa; border: 1px solid #e9ecef; }
                .rounded-3 { border-radius: 12px !important; }
                .fs-10 { font-size: 0.625rem; }
                .fs-14 { font-size: 0.875rem; }
                .fs-20 { font-size: 1.25rem; }
                .fs-24 { font-size: 1.5rem; }
                .fs-48 { font-size: 3rem; }
                .detail-modal .modal-content { border: none; border-radius: 24px; overflow: hidden; }
                
                /* Checkbox arrondie */
                .rounded-checkbox {
                    width: 18px;
                    height: 18px;
                    border-radius: 6px !important;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                .rounded-checkbox:checked {
                    background-color: #4361ee;
                    border-color: #4361ee;
                }
                .rounded-checkbox:indeterminate {
                    background-color: #4361ee;
                    border-color: #4361ee;
                }
            `}</style>
        </>
    );
}